import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  Zap, Play, Plus, RefreshCw, ChevronRight, Bell
} from 'lucide-react';
import SectionHelp from '@/components/help/SectionHelp';
import helpContent from '@/help/helpContent';
import api, { formatApiError } from '@/lib/api';
import AutomationsMap from '@/components/AutomationsMap';

const EVENT_LABELS = {
  'crm.lead.assigned': 'Lead asignado a vendedor',
  'crm.task.due_today': 'Tarea CRM vence hoy',
  'lead.birthday_today': 'Cumpleaños del lead hoy',
  'lead.reactivation_due': 'Lead inactivo (reactivación)',
  'lead.expiry_approaching': 'Vencimiento próximo del lead',
  'deal.stage_changed': 'Oportunidad cambió de etapa',
  'deal.stage_sla_breached': 'Oportunidad atorada en etapa',
  'agenda.appointment.created': 'Cita creada',
  'agenda.appointment.reminder_due': 'Recordatorio de cita',
};

// Sprint 53 (15/N) — "create_crm_task" se quitó: no tenía ningún manejador
// en el backend ni forma de seleccionarse al crear una automatización, era
// un botón muerto. "notify_assigned_user_email" es real y nueva — antes
// Automatizaciones solo podía avisar por WhatsApp.
const ACTION_LABELS = {
  'notify_assigned_user_whatsapp': 'Notificar por WhatsApp al vendedor',
  'notify_lead_whatsapp': 'Recordatorio WhatsApp al lead (con consentimiento)',
  'notify_assigned_user_email': 'Notificar por correo al vendedor',
  'log_activity': 'Registrar actividad en CRM',
};

const triggersHelp = helpContent.triggers || {};

const STATUS_STYLES = {
  success: 'bg-emerald-100 text-emerald-700',
  failed: 'bg-red-100 text-red-700',
  skipped: 'bg-amber-100 text-amber-700',
  duplicate: 'bg-gray-100 text-gray-500',
  running: 'bg-blue-100 text-blue-700',
  pending: 'bg-gray-100 text-gray-500',
};

const STATUS_LABELS_ES = {
  success: 'Exitoso', failed: 'Fallido', skipped: 'Omitido',
  duplicate: 'Ya ejecutado', running: 'En proceso', pending: 'Pendiente',
};

function StatusBadge({ status }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${STATUS_STYLES[status] || 'bg-gray-100 text-gray-500'}`}
          data-testid={`trigger-status-${status}`}>
      {STATUS_LABELS_ES[status] || status}
    </span>
  );
}

export default function TriggerCenter() {
  useAuth(); // cookie session — multi-vertical
  const [triggers, setTriggers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTrigger, setSelectedTrigger] = useState(null);
  const [runs, setRuns] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [pipelineStages, setPipelineStages] = useState([]);
  const [stageForNotify, setStageForNotify] = useState('');
  const [creatingStageTrigger, setCreatingStageTrigger] = useState(false);

  const loadTriggers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/tenant/triggers');
      setTriggers(data || []);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { loadTriggers(); }, [loadTriggers]);

  // 2026-07-24 — "avisar al cliente cuando el pipeline cambia de etapa" ya
  // funcionaba en el backend (evento deal.stage_changed + acción
  // notify_lead_whatsapp + filtro conditions_json.new_stage_key), pero no
  // había forma de crearlo desde aquí — la única UI eran 3 plantillas fijas.
  // Se carga el pipeline REAL del tenant (sus propias etapas, cualquiera sea
  // su vertical — talleres, hoteles, genérico) en vez de una lista fija.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/tenant/crm/pipelines/default');
        setPipelineStages(data?.stages || []);
      } catch {}
    })();
  }, []);

  const existingStageKeys = new Set(
    triggers.filter(t => t.event_type === 'deal.stage_changed').map(t => t.conditions_json?.new_stage_key).filter(Boolean)
  );
  const availableStages = pipelineStages.filter(s => !existingStageKeys.has(s.key));

  const createStageChangeTrigger = async () => {
    const stage = pipelineStages.find(s => s.key === stageForNotify);
    if (!stage) return;
    setCreatingStageTrigger(true);
    try {
      await api.post('/tenant/triggers', {
        key: `notify_lead_stage_${stage.key}`,
        name: `Avisar al cliente: etapa "${stage.label || stage.key}"`,
        description: 'Envía un WhatsApp al cliente cuando su oportunidad entra a esta etapa (requiere consentimiento activo).',
        event_type: 'deal.stage_changed',
        is_enabled: true,
        conditions_json: { new_stage_key: stage.key },
        actions_json: [
          { type: 'notify_lead_whatsapp', template: '' },
          { type: 'log_activity', summary: `Cliente avisado: etapa "${stage.label || stage.key}"` },
        ],
        cooldown_seconds: 60,
      });
      loadTriggers();
      setStageForNotify('');
    } catch (err) {
      alert(formatApiError(err?.response?.data?.detail) || 'Error al crear');
    }
    setCreatingStageTrigger(false);
  };

  const loadRuns = async (triggerId) => {
    try {
      const { data } = await api.get(`/tenant/triggers/${triggerId}/runs`, { params: { limit: 20 } });
      setRuns(data || []);
    } catch {}
  };

  const toggleTrigger = async (trigger) => {
    try {
      await api.put(`/tenant/triggers/${trigger.id}`, { is_enabled: !trigger.is_enabled });
      loadTriggers();
    } catch {}
  };

  const createTriggerFromTemplate = async (templateKey) => {
    const templates = {
      lead_assigned: {
        key: 'notify_assigned_user_on_lead',
        name: 'Notificar vendedor cuando se le asigne un lead',
        event_type: 'crm.lead.assigned',
        is_enabled: false,
        conditions_json: { assigned_to_user_id: '__not_null__' },
        actions_json: [
          { type: 'notify_assigned_user_whatsapp', template: '' },
          { type: 'log_activity', summary: 'Trigger ejecutado: lead asignado a {{assigned_user_name}}' },
        ],
        cooldown_seconds: 60,
      },
      appointment_reminder: {
        key: 'notify_appointment_reminder',
        name: 'Recordatorio de cita (vendedor + lead)',
        description: 'Avisa al vendedor siempre. Al lead solo si tiene consentimiento WhatsApp.',
        event_type: 'agenda.appointment.reminder_due',
        is_enabled: true,
        conditions_json: {},
        actions_json: [
          { type: 'notify_assigned_user_whatsapp', template: '' },
          { type: 'notify_lead_whatsapp', template: '' },
          { type: 'log_activity', summary: 'Recordatorio de cita ({{reminder_hours}}h antes) — {{lead_name}}' },
        ],
        cooldown_seconds: 300,
      },
      // Sprint 53 (15/N) — antes Automatizaciones solo podía avisar por
      // WhatsApp; esta plantilla usa el nuevo canal de correo real.
      lead_assigned_email: {
        key: 'notify_assigned_user_email_on_lead',
        name: 'Notificar vendedor por correo cuando se le asigne un lead',
        event_type: 'crm.lead.assigned',
        is_enabled: false,
        conditions_json: { assigned_to_user_id: '__not_null__' },
        actions_json: [
          { type: 'notify_assigned_user_email', template: '' },
          { type: 'log_activity', summary: 'Trigger ejecutado: lead asignado a {{assigned_user_name}} (aviso por correo)' },
        ],
        cooldown_seconds: 60,
      },
    };
    const body = templates[templateKey];
    if (!body) return;
    try {
      await api.post('/tenant/triggers', body);
      loadTriggers();
      setShowCreate(false);
    } catch (err) {
      alert(formatApiError(err?.response?.data?.detail) || 'Error al crear');
    }
  };

  const simulate = async (trigger) => {
    try {
      const { data } = await api.post('/tenant/triggers/simulate', {
        event_type: trigger.event_type,
        payload: { assigned_to_user_id: 'test', lead_name: 'Lead de prueba' },
      });
      alert(`Simulación: ${data.would_execute} automatización(es) aplicarían.\n${data.note}`);
    } catch {}
  };

  const selectTrigger = (t) => {
    setSelectedTrigger(t);
    loadRuns(t.id);
  };

  return (
    <div data-testid="trigger-center" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-[#0F172A]">Reglas al instante</h2>
          <p className="text-sm text-[#64748B]">Configura acciones automáticas cuando ocurren eventos en tu CRM</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => createTriggerFromTemplate('lead_assigned')} data-testid="create-template-trigger-btn">
            <Plus className="w-4 h-4 mr-1" /> Lead asignado (WhatsApp)
          </Button>
          <Button size="sm" variant="outline" onClick={() => createTriggerFromTemplate('lead_assigned_email')} data-testid="create-email-trigger-btn">
            <Plus className="w-4 h-4 mr-1" /> Lead asignado (correo)
          </Button>
          <Button size="sm" onClick={() => createTriggerFromTemplate('appointment_reminder')} data-testid="create-reminder-trigger-btn">
            <Bell className="w-4 h-4 mr-1" /> Recordatorio cita
          </Button>
        </div>
      </div>

      {pipelineStages.length > 0 && (
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 flex flex-wrap items-center gap-2" data-testid="stage-notify-bar">
          <span className="text-sm text-[#334155] font-medium">Avisar al cliente cuando su oportunidad cambie a:</span>
          <select
            className="text-sm border border-[#E2E8F0] rounded-lg px-2 py-1.5 text-[#0F172A]"
            value={stageForNotify}
            onChange={(e) => setStageForNotify(e.target.value)}
            data-testid="stage-notify-select"
          >
            <option value="">Elegir etapa…</option>
            {availableStages.map(s => (
              <option key={s.key} value={s.key}>{s.label || s.key}</option>
            ))}
          </select>
          <Button size="sm" onClick={createStageChangeTrigger} disabled={!stageForNotify || creatingStageTrigger} data-testid="create-stage-notify-btn">
            <Bell className="w-4 h-4 mr-1" /> {creatingStageTrigger ? 'Creando…' : 'Crear automatización'}
          </Button>
          {availableStages.length === 0 && (
            <span className="text-xs text-[#94A3B8]">Ya tienes una automatización para cada etapa de tu pipeline.</span>
          )}
        </div>
      )}

      <AutomationsMap current="rules" />
      <SectionHelp
        module="triggers"
        title={triggersHelp.title || 'las automatizaciones'}
        description={triggersHelp.description}
        whatItDoesNot={triggersHelp.whatItDoesNot}
      />

      {/* Triggers List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-5 h-5 text-[#94A3B8] animate-spin" />
        </div>
      ) : triggers.length === 0 ? (
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-8 text-center" data-testid="empty-triggers">
          <Zap className="w-10 h-10 text-[#CBD5E1] mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-sm text-[#64748B] mb-4">No hay automatizaciones configuradas</p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <Button size="sm" variant="outline" onClick={() => createTriggerFromTemplate('lead_assigned')} data-testid="create-first-trigger-btn">
              <Plus className="w-4 h-4 mr-1" /> Lead asignado
            </Button>
            <Button size="sm" onClick={() => createTriggerFromTemplate('appointment_reminder')} data-testid="create-first-reminder-trigger-btn">
              <Bell className="w-4 h-4 mr-1" /> Recordatorio de cita
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {triggers.map((t) => (
            <div key={t.id} className="bg-white border border-[#E2E8F0] rounded-xl p-4 hover:border-[#CBD5E1] transition-colors"
                 data-testid={`trigger-card-${t.key}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer" onClick={() => selectTrigger(t)}>
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${t.is_enabled ? 'bg-emerald-50' : 'bg-gray-50'}`}>
                    <Zap className={`w-4 h-4 ${t.is_enabled ? 'text-emerald-600' : 'text-gray-400'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-medium text-[#0F172A] truncate">{t.name}</h3>
                    <p className="text-xs text-[#64748B]">
                      {EVENT_LABELS[t.event_type] || t.event_type} &middot; {(t.actions_json || []).map(a => ACTION_LABELS[a.type] || a.type).join(', ')}
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-[#94A3B8] flex-shrink-0" />
                </div>
                <div className="flex items-center gap-3 ml-3">
                  <Button variant="ghost" size="sm" onClick={() => simulate(t)} data-testid={`simulate-trigger-${t.key}`}>
                    <Play className="w-3.5 h-3.5 mr-1" /> Simular
                  </Button>
                  <Switch
                    checked={t.is_enabled}
                    onCheckedChange={() => toggleTrigger(t)}
                    data-testid={`toggle-trigger-${t.key}`}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Selected Trigger Runs */}
      {selectedTrigger && (
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-4" data-testid="trigger-runs-panel">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-[#0F172A]">
              Ejecuciones: {selectedTrigger.name}
            </h3>
            <Button variant="ghost" size="sm" onClick={() => loadRuns(selectedTrigger.id)}>
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
          </div>
          {runs.length === 0 ? (
            <p className="text-sm text-[#64748B] py-4 text-center">No hay ejecuciones registradas</p>
          ) : (
            <div className="space-y-2">
              {runs.map((r) => (
                <div key={r.id} className="flex items-center justify-between py-2 border-b border-[#F1F5F9] last:border-0"
                     data-testid={`trigger-run-${r.id}`}>
                  <div className="flex items-center gap-3">
                    <StatusBadge status={r.status} />
                    <span className="text-xs text-[#64748B]">{r.event_ref_id?.substring(0, 8)}...</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {r.error_message && (
                      <span className="text-[10px] text-red-500 max-w-[200px] truncate">{r.error_message}</span>
                    )}
                    <span className="text-[10px] text-[#94A3B8]">
                      {r.created_at && new Date(r.created_at).toLocaleString('es-MX')}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
